import {Injectable} from '@angular/core';
import {gql} from 'apollo-angular';
import {FilterValue, ReportFilters} from 'app/features/analytics/service/filter-value.interface';
import {ApiService} from 'app/shared/modules/api/services/api.service';
import {
    savedFilters,
    savedFilters_analytics,
    savedFilters_analytics_savedFilters,
    savedFilters_analytics_savedFilters_nodes,
} from 'app/features/analytics/service/gql/savedFilters';
import {cloneDeep} from 'app/core/helpers/object';
import {DateDimension, SavedDateFilterPeriods} from '../../../../globalTypes';
import {DateRange, DateRangeValue, DefaultDateRange} from 'app/shared/classes/date-range';

import {Observable} from 'rxjs';
import {waitFirstEmit} from 'app/core/helpers/observable';
import {map, take} from 'rxjs/operators';
import {saveFilters, saveFilters_reportFilters_saveFilters} from 'app/features/analytics/service/gql/saveFilters';
import {updateFilters} from 'app/features/analytics/service/gql/updateFilters';

export type SavedFilterNodes = savedFilters_analytics_savedFilters_nodes;

export const savedFilterAttributesFragment = gql`
    fragment SavedFilterAttributes on SavedFilter {
        id
        name
        activityFilter
        installFilter
        filters
    }
`;

const savedFiltersQuery = gql`
    query savedFilters {
        analytics {
            savedFilters {
                nodes {
                    ...SavedFilterAttributes
                }
            }
        }
    }
`;

const saveFiltersMutation = gql`
    mutation saveFilters($attributes: SaveReportFilterAttributes!) {
        reportFilters {
            saveFilters(attributes: $attributes) {
                ...SavedFilterAttributes
            }
        }
    }
`;

const updateFiltersMutation = gql`
    mutation updateFilters($attributes: UpdateReportFilterAttributes!, $id: ID!) {
        reportFilters {
            updateFilters(attributes: $attributes, id: $id) {
                ...SavedFilterAttributes
            }
        }
    }
`;

const deleteFilters = gql`
    mutation deleteFilters($savedFilterId: ID!) {
        reportFilters {
            deleteFilters(id: $savedFilterId)
        }
    }
`;

@Injectable({
    providedIn: 'root',
})
export class SavedFilterService {
    constructor(private api: ApiService) {
        this.api.registerFragments(savedFilterAttributesFragment);
    }

    resolve(): Promise<Observable<SavedFilterNodes[]>> {
        return waitFirstEmit(
            this.api
                .watchQuery<savedFilters>({
                    query: savedFiltersQuery,
                })
                .valueChanges.pipe(map(r => r.analytics.savedFilters.nodes))
        );
    }

    create(params: Partial<SavedFilterNodes>): Observable<SavedFilterNodes> {
        return this.api.mutate({
            mutation: saveFiltersMutation,
            variables: {
                attributes: {
                    name: params.name,
                    activityFilter: params.activityFilter || null,
                    installFilter: params.installFilter || null,
                    filters: params.filters,
                },
            },
            update: this.api.cacheHelper.updateQuery(
                {
                    query: savedFiltersQuery,
                },
                (
                    cache: {
                        analytics: savedFilters_analytics;
                        savedFilters: savedFilters_analytics_savedFilters;
                        nodes: savedFilters_analytics_savedFilters_nodes[];
                    },
                    {reportFilters}: saveFilters
                ) => {
                    const savedFilter: saveFilters_reportFilters_saveFilters = reportFilters.saveFilters;
                    if (savedFilter) {
                        cache.analytics.savedFilters.nodes.push(savedFilter as any);
                    }
                    return cache;
                }
            ),
        }) as Observable<SavedFilterNodes>;
    }

    update(params: Partial<SavedFilterNodes>): Observable<SavedFilterNodes> {
        return this.api.mutate({
            mutation: updateFiltersMutation,
            variables: {
                id: params.id,
                attributes: {
                    filters: params.filters,
                },
            },
            update: this.api.cacheHelper.updateQuery(
                {
                    query: savedFiltersQuery,
                },
                (
                    cache: {
                        analytics: savedFilters_analytics;
                        savedFilters: savedFilters_analytics_savedFilters;
                        nodes: savedFilters_analytics_savedFilters_nodes[];
                    },
                    {reportFilters}: updateFilters
                ) => {
                    const updatedFilter = reportFilters.updateFilters;
                    const index = cache.analytics.savedFilters.nodes.findIndex(node => node.id === updatedFilter.id);
                    if (index !== -1) {
                        cache.analytics.savedFilters.nodes[index] = updatedFilter;
                    }
                    return cache;
                }
            ),
        }) as Observable<SavedFilterNodes>;
    }

    rename(params: Partial<SavedFilterNodes>): Observable<SavedFilterNodes> {
        return this.api.mutate({
            mutation: updateFiltersMutation,
            variables: {
                id: params.id,
                attributes: {
                    name: params.name,
                },
            },
            update: this.api.cacheHelper.updateQuery(
                {
                    query: savedFiltersQuery,
                },
                (
                    cache: {
                        analytics: savedFilters_analytics;
                        savedFilters: savedFilters_analytics_savedFilters;
                        nodes: savedFilters_analytics_savedFilters_nodes[];
                    },
                    {reportFilters}
                ) => {
                    const updatedFilter = reportFilters.updateFilters;
                    const index = cache.analytics.savedFilters.nodes.findIndex(node => node.id === updatedFilter.id);
                    if (index !== -1) {
                        cache.analytics.savedFilters.nodes[index] = updatedFilter;
                    }
                    return cache;
                }
            ),
        }) as Observable<SavedFilterNodes>;
    }

    delete(savedFilter: SavedFilterNodes): Promise<any> {
        return this.api
            .mutate({
                mutation: deleteFilters,
                variables: {
                    savedFilterId: savedFilter.id,
                },
                update: this.api.cacheHelper.removeEntity(savedFilter),
            })
            .toPromise();
    }

    getSavedFilters(): Observable<SavedFilterNodes[]> {
        return this.api
            .watchQuery<savedFilters>({
                query: savedFiltersQuery,
            })
            .valueChanges.pipe(map(r => r.analytics.savedFilters.nodes));
    }

    findSavedFilter(id): Promise<ReportFilters | null> {
        return this.getSavedFilters()
            .pipe(
                take(1),
                map(reports => reports.find(r => r.id === id)),
                map(v => (v ? this.getFilterWithNewDate(v) : null))
            )
            .toPromise();
    }

    getFilterWithNewDate(filterNode: SavedFilterNodes): ReportFilters {
        if (this.checkExactDate(filterNode.activityFilter, filterNode.installFilter)) {
            return filterNode.filters;
        }

        let getNewDate = cloneDeep(filterNode.filters.filter);
        let reportFilters: ReportFilters = filterNode.filters;

        if (filterNode.activityFilter !== null) {
            getNewDate = [...this.setUpdatesDateFilter(reportFilters.filter, filterNode.activityFilter)];
        }

        if (filterNode.installFilter !== null) {
            getNewDate = [...this.setUpdatesDateFilter(getNewDate, filterNode.installFilter, DateDimension.INSTALL_DATE)];
        }

        return {
            ...(filterNode.id ? {savedReportId: filterNode.id} : {}),
            ...reportFilters,
            filter: [...getNewDate],
        };
    }

    checkExactDate(dateActivity: string, dateInstall: string): boolean {
        return (
            typeof (dateActivity || dateInstall) === 'string' &&
            (String(dateActivity) === SavedDateFilterPeriods.EXACT_DATES || String(dateInstall) === SavedDateFilterPeriods.EXACT_DATES)
        );
    }

    setUpdatesDateFilter(reportFilter: FilterValue[], nameFilter: string, defaultDateDimension = DateDimension.DATE): FilterValue[] {
        let indx = reportFilter.findIndex(e => e.id === defaultDateDimension);
        let newReports = cloneDeep(reportFilter);

        if (indx !== -1) {
            newReports.splice(indx, 1, this.setRangeDateFilter(reportFilter, nameFilter, defaultDateDimension));
            return newReports;
        }

        return [{...this.setRangeDateFilter(reportFilter, nameFilter, defaultDateDimension)}];
    }

    setRangeDateFilter(reportFilter: FilterValue[], typeFilterDate: string, defaultDateDimension = DateDimension.DATE) {
        let dateRange: DateRangeValue = this.getDefaultRangeDate(typeFilterDate);

        if (reportFilter.some(id => id.id === defaultDateDimension)) {
            return {
                id: reportFilter.find(id => id.id === defaultDateDimension).id,
                from: dateRange.start,
                to: dateRange.end,
            };
        }

        return {
            id: defaultDateDimension,
            from: dateRange.start,
            to: dateRange.end,
        };
    }

    getDefaultRangeDate(range: string) {
        switch (range) {
            case SavedDateFilterPeriods.LAST_30_DAYS:
                return this.setDefaultRange(DefaultDateRange.last30Days);

            case SavedDateFilterPeriods.LAST_60_DAYS:
                return this.setDefaultRange(DefaultDateRange.last60Days);

            case SavedDateFilterPeriods.LAST_90_DAYS:
                return this.setDefaultRange(DefaultDateRange.last90Days);

            case SavedDateFilterPeriods.LAST_7_DAYS:
                return this.setDefaultRange(DefaultDateRange.last7Days);

            case SavedDateFilterPeriods.LAST_14_DAYS:
                return this.setDefaultRange(DefaultDateRange.last14Days);

            case SavedDateFilterPeriods.LAST_WEEK:
                return this.setDefaultRange(DefaultDateRange.lastWeek);

            case SavedDateFilterPeriods.LAST_MONTH:
                return this.setDefaultRange(DefaultDateRange.lastMonth);

            case SavedDateFilterPeriods.LAST_QUARTER:
                return this.setDefaultRange(DefaultDateRange.lastQuarter);

            case SavedDateFilterPeriods.LAST_YEAR:
                return this.setDefaultRange(DefaultDateRange.lastYear);

            case SavedDateFilterPeriods.THIS_WEEK:
                return this.setDefaultRange(DefaultDateRange.thisWeek);

            case SavedDateFilterPeriods.THIS_MONTH:
                return this.setDefaultRange(DefaultDateRange.thisMonth);

            case SavedDateFilterPeriods.THIS_QUARTER:
                return this.setDefaultRange(DefaultDateRange.thisQuarter);

            case SavedDateFilterPeriods.THIS_YEAR:
                return this.setDefaultRange(DefaultDateRange.thisYear);

            case SavedDateFilterPeriods.ALL_TIME:
                return this.setDefaultRange(DefaultDateRange.allTime);

            case SavedDateFilterPeriods.YESTERDAY:
                return this.setDefaultRange(DefaultDateRange.yesterday);

            case SavedDateFilterPeriods.TODAY:
                return this.setDefaultRange(DefaultDateRange.today);
        }
    }

    setDefaultRange(range: DefaultDateRange) {
        return DateRange.createRangeValue(range);
    }
}
